import { normalizeFileType } from '@magicschool/spellbook/components/FileItem';
import mime from 'mime/lite';
import type { Accept, FileError, FileRejection } from 'react-dropzone';
import type { InitialFile } from './types';

// `mime/lite` (kept for bundle size — it's imported by every Dropzone consumer) omits Office
// types, so `.docx`/`.pptx`/`.xlsx` don't resolve. Layer a tiny explicit map for just those,
// rather than pulling the full `mime` database into every client bundle for a few extensions.
const OFFICE_EXTENSION_MIME_TYPES: Record<string, string> = {
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/** Formats bytes to human-readable size (e.g., "1.5 MB") */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
}

/** Gets file extension without dot (e.g., "pdf") */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? (parts[parts.length - 1] ?? '').toLowerCase() : '';
}

/** Generates unique key for file in list */
export function getFileKey(file: File, index?: number): string {
  return `${file.name}-${file.size}-${file.lastModified}-${index ?? ''}`;
}

/** Checks if a file is an image. Uses MIME type for File objects; resolves extension via `mime` for InitialFile. */
export function isImageFile(file: File | InitialFile): boolean {
  if (file instanceof File) {
    return file.type.startsWith('image/');
  }
  if (file.fileType.startsWith('image/')) return true;
  const mimeType = mime.getType(normalizeFileType(file.fileType));
  return mimeType?.startsWith('image/') ?? false;
}

/**
 * Converts a flat array of extensions, MIME types, and/or wildcards
 * into react-dropzone's `Accept` format.
 *
 * - **Extensions** (recommended, e.g., `'.jpg'`): resolve to both MIME type
 *   and extension, giving the broadest file-matching coverage.
 * - **MIME types** (e.g., `'application/pdf'`): match by MIME type with the
 *   corresponding extension auto-resolved.
 * - **Wildcards** (e.g., `'image/*'`): match by MIME type only. Won't catch
 *   files whose MIME type is missing (possible on drag-and-drop). Use only
 *   when you truly want to accept *every* subtype of a category.
 *
 * @example
 * // Extensions (recommended)
 * buildAccept(['.pdf', '.docx'])
 * // → { 'application/pdf': ['.pdf'], 'application/vnd...officedocument...': ['.docx'] }
 *
 * @example
 * // MIME types
 * buildAccept(['application/pdf', 'image/png'])
 * // → { 'application/pdf': ['.pdf'], 'image/png': ['.png'] }
 *
 * @example
 * // Wildcards
 * buildAccept(['image/*'])
 * // → { 'image/*': [] }
 */
export function buildAccept(types: string[]): Accept {
  const accept: Record<string, string[]> = {};

  for (const type of types) {
    if (type.includes('/')) {
      const ext = type.endsWith('/*') ? undefined : mime.getExtension(type);
      accept[type] = ext ? [`.${ext}`] : [];
    } else {
      const ext = type.startsWith('.') ? type : `.${type}`;
      const mimeType = mime.getType(ext) ?? OFFICE_EXTENSION_MIME_TYPES[ext.toLowerCase()];
      if (!mimeType) continue;
      if (!accept[mimeType]) accept[mimeType] = [];
      accept[mimeType].push(ext);
    }
  }

  return accept;
}

/**
 * Whether the Preview slot is displaying an error for this file.
 * Centralizes the "single image file with error" check used by both
 * autoErrorMessage and dragRejectMessage logic in Dropzone.tsx.
 */
export function isPreviewShowingError(files: File[], validationState?: { status: string }): boolean {
  if (files.length !== 1) return false;
  const file = files[0];
  if (!file?.type.startsWith('image/')) return false;
  return validationState?.status === 'error';
}

/** Extracts File objects from a clipboard paste event */
export function getFilesFromClipboard(event: ClipboardEvent): File[] {
  const items = event.clipboardData?.items;
  if (!items) return [];

  const files: File[] = [];
  for (const item of items) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

/** Checks if a file matches an Accept spec (MIME types + extensions) */
function matchesAcceptedType(file: File, accept: Accept): boolean {
  const fileType = file.type;
  const rawExtension = getFileExtension(file.name);
  const fileExtension = rawExtension ? `.${rawExtension}` : '';

  return Object.entries(accept).some(([type, extensions]) => {
    if (type.endsWith('/*')) {
      return fileType.startsWith(type.slice(0, -2));
    }
    if (type === fileType) return true;
    return extensions?.some((ext) => ext.toLowerCase() === fileExtension);
  });
}

interface PasteValidationOptions {
  accept?: Accept;
  maxSize?: number;
  maxFiles?: number;
  multiple?: boolean;
  currentFileCount?: number;
}

interface PasteValidationResult {
  accepted: File[];
  rejected: FileRejection[];
}

/** Validates pasted files against accept, maxSize, and maxFiles constraints */
export function validatePastedFiles(files: File[], options: PasteValidationOptions): PasteValidationResult {
  const { accept, maxSize, maxFiles, multiple, currentFileCount } = options;
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];

  for (const file of files) {
    const errors: FileError[] = [];

    if (accept && !matchesAcceptedType(file, accept)) {
      errors.push({ code: 'file-invalid-type', message: 'File type not accepted' });
    }

    if (maxSize && file.size > maxSize) {
      errors.push({ code: 'file-too-large', message: 'File is too large' });
    }

    const wouldExceedMax = maxFiles && (currentFileCount ?? 0) + accepted.length + 1 > maxFiles;
    // In single mode, only limit within the pasted batch (accept 1, reject rest).
    // Existing files get replaced on paste, matching react-dropzone's drop behavior.
    const wouldExceedSingle = !multiple && accepted.length >= 1;
    if (wouldExceedMax || wouldExceedSingle) {
      errors.push({ code: 'too-many-files', message: 'Too many files' });
    }

    if (errors.length === 0) {
      accepted.push(file);
    } else {
      rejected.push({ file, errors });
    }
  }

  const finalAccepted = multiple ? accepted : accepted.slice(0, 1);
  return { accepted: finalAccepted, rejected };
}

/** Triggers a browser download for a blob URL. */
export function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Error text overrides for getErrorMessage */
interface ErrorTextOverrides {
  fileTooLarge?: (maxSize: string) => string;
  /** Static string or a function receiving the rejected filename for a contextual message. */
  fileInvalidType?: string | ((fileName: string) => string);
  tooManyFiles?: string;
}

const ERROR_CODES = {
  FileTooLarge: 'file-too-large',
  FileInvalidType: 'file-invalid-type',
  TooManyFiles: 'too-many-files',
} as const;

/** Converts a file rejection to a user-friendly error message */
export function getErrorMessage(
  rejection: { errors: readonly { code: string; message: string }[]; file?: { name: string } },
  text?: ErrorTextOverrides,
  config?: { maxSize?: number },
): string {
  const error = rejection.errors[0];
  if (!error) return 'Invalid file';

  const messageByCode: Record<string, () => string> = {
    [ERROR_CODES.FileTooLarge]: () => {
      if (text?.fileTooLarge) return text.fileTooLarge(formatFileSize(config?.maxSize ?? 0));
      return config?.maxSize ? `File exceeds the ${formatFileSize(config.maxSize)} size limit` : 'File is too large';
    },
    [ERROR_CODES.FileInvalidType]: () => {
      const typeText = text?.fileInvalidType;
      if (typeof typeText === 'function') return typeText(rejection.file?.name ?? '');
      return typeText ?? 'This file type is not accepted';
    },
    [ERROR_CODES.TooManyFiles]: () => text?.tooManyFiles ?? 'Too many files selected',
  };

  return messageByCode[error.code]?.() ?? 'Invalid file';
}
